import type { ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import type { CrmBulkFailure } from "./crmBulkCopy";
import { formatCount } from "./crmBulkCopy";

/**
 * The shell every bulk confirmation shares: the audience line, the refusal surface and
 * the two buttons. The four dialogs differ only in what they ASK for, so everything they
 * do identically lives here — a delete that reports its refusal differently from an
 * export is how one of them ends up swallowing a drift warning.
 *
 * ── Three behaviours that are not decoration ──────────────────────────────────────
 *
 *  1. THE DIALOG DOES NOT CLOSE ON FAILURE. `describeCrmBulkFailure` produces the one
 *     sentence the realtor can act on ("narrow the filter", "the list changed"), and a
 *     dialog that closes and drops a toast puts that sentence 6 seconds from being gone.
 *     It closes on SUCCESS, where the result summary is a receipt, not an instruction.
 *  2. `requiresReview` DISABLES THE CONFIRM BUTTON. A drift refusal means the server and
 *     the browser disagree about what is selected; pressing "confirm" again just sends
 *     the same stale `expectedCount` and gets the same refusal. The only useful next move
 *     is to close, let the list reload and look again — so that is the only move offered.
 *  3. THE COUNT IS RENDERED FROM WHAT THE CALLER PASSES, which is the same number that
 *     rides as `expectedCount`. It is deliberately not recomputed here: the whole
 *     drift-detection contract rests on the acted-on number being the DISPLAYED one.
 */

type Props = {
  open: boolean;
  title: string;
  /** "8,102 לקוחות" — always the selection's own count, never a re-derived one. */
  count: number;
  /** What is about to happen, in one or two sentences. */
  children: ReactNode;
  confirmLabel: string;
  confirmColor?: "primary" | "error";
  busy: boolean;
  /** Non-null after a refusal. Keeps the dialog open and, when severe, blocks the retry. */
  failure: CrmBulkFailure | null;
  /** Blocks the confirm for a reason the caller knows about (an empty patch, a cap). */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

export default function CrmBulkDialog({
  open,
  title,
  count,
  children,
  confirmLabel,
  confirmColor = "primary",
  busy,
  failure,
  confirmDisabled,
  onConfirm,
  onClose
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      /* Backdrop click, Escape and the cancel button all close it — the same way every
         other dialog in the app closes, which is what the pilot user expected of these too
         (reported 2026-09-06: "clicking outside a modal needs to close that modal"). It used
         to swallow the backdrop click on the theory that a destructive action should not be
         dismissed by accident; in practice a dialog that ignores a click beside it reads as
         stuck, and closing is the SAFE direction — nothing has been sent yet.

         The one refusal that stays: WHILE A REQUEST IS IN FLIGHT nothing closes it. The
         server has already been asked, and a dialog that vanishes mid-delete leaves the
         realtor with no idea whether it happened. */
      onClose={() => {
        if (busy) return;
        onClose();
      }}
      fullWidth
      maxWidth="xs"
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6">{title}</Typography>
          {/* Every number in this UI is LTR digits inside RTL Hebrew, in its own atomic
              box — a bare "8,102" laid out with the surrounding Hebrew puts the comma
              group in the wrong place. `dir` is the ATTRIBUTE, never `sx={{ direction }}`:
              stylis-plugin-rtl rewrites the CSS property to its mirror, so an sx-declared
              `direction: 'ltr'` ships as `direction: rtl`. */}
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <Box component="span" dir="ltr" sx={{ display: "inline-block", fontWeight: 700 }}>
              {formatCount(count)}
            </Box>{" "}
            לקוחות נבחרו
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {children}
          {failure && (
            <Alert severity={failure.requiresReview ? "warning" : "error"}>{failure.message}</Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={busy}>
          {failure?.requiresReview ? "סגירה ורענון" : "ביטול"}
        </Button>
        <Button
          variant="contained"
          color={confirmColor}
          onClick={onConfirm}
          disabled={busy || confirmDisabled || Boolean(failure?.requiresReview)}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
